import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";
import axios from "axios";
import { slugSplitter } from "../../../utils/slugManager";
import config from "../../../configuration/config";
import { useTranslation } from "react-i18next";
import ReactWhatsapp from "react-whatsapp";
import { groupBy, mapValues, omit } from "lodash";
import { Price } from "../../../components/__simple/_price";
import { generateVehicleSpecsPDF } from "../../../utils/generateVehicleSpecsPDF";
import Skeleton from "react-loading-skeleton";
import { EmptyState } from "../../../components/EmptyState";
import { Swiper, SwiperSlide } from "swiper/react";

export function VehicleSpecsPage({ modelslug, vehicle }) {
    const { t: trans, i18n } = useTranslation(["common", "vehicales", "info", "car"]);
    const { slug } = useParams();
    const [models, setModels] = useState([]);
    const [selectedModelId, setSelectedModelId] = useState(null);
    const [backUpImage, setBackUpImage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    let lang = (i18n.language || 'ar');

    // Comparison Engine State
    const [allFeatures, setAllFeatures] = useState({});
    const [openSecs, setOpenSecs] = useState(new Set());
    const [viewMode, setViewMode] = useState("single");
    const [filterType, setFilterType] = useState("all"); // 'all', 'hybrid', 'conventional'
    const [activeSpecCat, setActiveSpecCat] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [showDifferencesOnly] = useState(false);
    const [isPdfReady, setIsPdfReady] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const callback = useCallback(async (vehicle) => {
        setIsLoading(true);
        try {
            setModels([]);
            setBackUpImage("");

            const filter1L5 = (modelsList) => {
                if (!modelsList) return [];
                return modelsList.filter(m => {
                    const combinedFields = `${m.modelDescription} ${m.name} ${m.type} ${m.ColorID} ${m.Reference} ${m.DescriptionEn} ${m.DescriptionAr}`.toLowerCase();
                    return !combinedFields.includes("1l5");
                });
            };

            try {
                if ("caches" in window) {
                    var curl = lang + "/api/VehicleSpecs/" + slugSplitter(vehicle?.group) + "/" + vehicle?.year + "/specs";
                    const cacheStorage = await caches.open("VehicleSpecsPage");
                    const cachedResponse = await cacheStorage.match(curl);
                    var datacash = await cachedResponse?.json();
                    if (datacash) {
                        const filteredCached = filter1L5(datacash.models);
                        setModels(filteredCached);
                        setBackUpImage(datacash.backupImage);
                    }
                }
            } catch (error) { }

            const response = await axios.request({
                baseURL: config.controllURL,
                url: lang + "/api/VehicleSpecs/" + slugSplitter(vehicle?.group) + "/" + vehicle?.year + "/specs",
                method: "get",
            });

            const filteredModels = filter1L5(response.data.models);
            setModels(filteredModels);
            setBackUpImage(response.data.backupImage);

            const dataResponse = new Response(JSON.stringify({ ...response.data, models: filteredModels }));
            if ("caches" in window) {
                caches.open("VehicleSpecsPage").then((cache) => {
                    cache.put(lang + "/api/VehicleSpecs/" + slugSplitter(vehicle?.group) + "/" + vehicle?.year + "/specs", dataResponse);
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }, [lang]);

    useEffect(() => {
        if (vehicle?.group)
            callback(vehicle);
    }, [slug, vehicle, callback]);

    // Initialize selection once models are fetched
    useEffect(() => {
        if (models.length > 0) {
            if (!selectedModelId) setSelectedModelId(models[0].productTarget);
        }
    }, [models, selectedModelId]);

    // Fetch all features for comparison
    useEffect(() => {
        if (models.length > 0) {
            models.forEach(async (model) => {
                if (!allFeatures[model.productTarget]) {
                    try {
                        const response = await axios.post(`${config.controllURL}${lang}/api/Pages/Fetch_InvProductsEquipments`, {
                            "GUID": `${model.productTarget}`
                        });
                        const groupResult = mapValues(groupBy(response.data, 'ID'), fList => fList.map(items => omit(items, 'ID')));
                        let dataFinal = [];
                        Object.keys(groupResult).forEach((key) => {
                            let item = null;
                            let combinedText = "";
                            groupResult[key].forEach((a, i) => {
                                // Important: Treat both Note and Description as valid source for spec lines
                                const textAr = [a.NoteAr, a.DescriptionAr].filter(t => t && t.trim()).join('\n').trim();
                                const textEn = [a.NoteEn, a.DescriptionEn].filter(t => t && t.trim()).join('\n').trim();

                                if (lang === "ar") {
                                    if (textAr) combinedText = combinedText + textAr + "\n";
                                } else {
                                    if (textEn) combinedText = combinedText + textEn + "\n";
                                }

                                if (i === (groupResult[key].length - 1)) {
                                    item = { ...a, note: combinedText.trim() };
                                }
                            });
                            if (item) dataFinal.push(item);
                        });
                        setAllFeatures(prev => ({ ...prev, [model.productTarget]: dataFinal }));

                        // Default open all sections (Normalized names)
                        if (dataFinal.length > 0) {
                            setOpenSecs(prev => {
                                const next = new Set(prev);
                                dataFinal.forEach(f => {
                                    const sName = (lang === 'ar' ? f.sectionAr : f.sectionEn)?.trim();
                                    if (sName) next.add(sName);
                                });
                                return next;
                            });
                        }
                    } catch (err) {
                        console.error("Failed to fetch features for " + model.productTarget, err);
                    }
                }
            });
        }
    }, [models, lang, allFeatures]);

    // Automatically select the most capable model only if none selected
    useEffect(() => {
        if (models.length > 0 && Object.keys(allFeatures).length === models.length && !selectedModelId) {
            let maxFeatures = -1;
            let bestModelId = models[0].productTarget;

            models.forEach(m => {
                const featureCount = allFeatures[m.productTarget]?.length || 0;
                if (featureCount > maxFeatures) {
                    maxFeatures = featureCount;
                    bestModelId = m.productTarget;
                }
            });

            setSelectedModelId(bestModelId);
        }
    }, [allFeatures, models, selectedModelId]);

    const activeModel = models?.find(m => m.productTarget === selectedModelId) || models[0] || vehicle;
    const mode = models.length <= 3 ? "few" : (models.length <= 6 ? "mid" : "many");

    // Unified Categories based on all fetched features
    const unifiedCats = useMemo(() => {
        const cats = [];
        Object.values(allFeatures).forEach(modelFeatures => {
            modelFeatures.forEach(f => {
                const sectionName = (lang === 'ar' ? f.sectionAr : f.sectionEn)?.trim();
                if (!sectionName) return;

                let cat = cats.find(c => c.title === sectionName);
                if (!cat) {
                    cat = { title: sectionName, rows: [] };
                    cats.push(cat);
                }

                const note = f.note || "";
                note.split('\n').forEach(line => {
                    const label = line.split(':')[0]?.trim();
                    if (label && label !== "null" && !cat.rows.includes(label)) {
                        cat.rows.push(label);
                    }
                });
            });
        });
        return cats;
    }, [allFeatures, lang]);

    // Specs Matrix for O(1) lookups and performance
    const specsMatrix = useMemo(() => {
        const matrix = {};
        Object.entries(allFeatures).forEach(([modelId, features]) => {
            matrix[modelId] = {};
            features.forEach(f => {
                const sectionName = (lang === 'ar' ? f.sectionAr : f.sectionEn)?.trim();
                if (!sectionName) return;
                if (!matrix[modelId][sectionName]) matrix[modelId][sectionName] = {};

                const note = f.note || "";
                note.split('\n').forEach(line => {
                    const parts = line.split(':');
                    const label = parts[0]?.trim();
                    if (label && label !== "null") {
                        matrix[modelId][sectionName][label] = parts.slice(1).join(':').trim() || '✓';
                    }
                });
            });
        });
        return matrix;
    }, [allFeatures, lang]);

    const getSpecValue = useCallback((modelTarget, sectionTitle, rowLabel) => {
        return specsMatrix[modelTarget]?.[sectionTitle]?.[rowLabel] || null;
    }, [specsMatrix]);

    // Filter models based on hybrid/conventional
    const filteredModels = useMemo(() => {
        if (filterType === 'hybrid') return models.filter(m => m.hybird || m.hybrid);
        if (filterType === 'conventional') return models.filter(m => !(m.hybird || m.hybrid));
        return models;
    }, [models, filterType]);

    // Check if we have both types to show filters
    const hasHybrid = useMemo(() => models.some(m => m.hybird || m.hybrid), [models]);
    const hasConventional = useMemo(() => models.some(m => !(m.hybird || m.hybrid)), [models]);
    const showTypeFilters = hasHybrid && hasConventional;
    const useTabbedView = useMemo(() => hasHybrid && models.length >= 7, [hasHybrid, models.length]);

    // Set default filter if in tabbed view
    useEffect(() => {
        if (useTabbedView && filterType === 'all') {
            setFilterType('hybrid');
        }
    }, [useTabbedView, filterType]);

    // Modes UI Helpers
    const visibleModels = viewMode === 'single' ? models.filter(m => m.productTarget === selectedModelId) : filteredModels;

    const filteredCatsForDisplay = useMemo(() => {
        // If not in Differences mode, or comparing only 1 model, show everything
        if (!showDifferencesOnly || visibleModels.length <= 1) return unifiedCats;

        return unifiedCats.map(cat => {
            const extraRows = cat.rows.filter(rowLabel => {
                let hasPresent = false;
                let hasAbsent = false;

                visibleModels.forEach(m => {
                    const rawVal = getSpecValue(m.productTarget, cat.title, rowLabel);
                    const isAbsent = (rawVal === null || rawVal === undefined || String(rawVal).trim() === '' || String(rawVal).trim() === '—' || String(rawVal).trim() === 'null');

                    if (isAbsent) hasAbsent = true;
                    else hasPresent = true;
                });

                // Logical "Extra" (الزيادات): Only show if some models have it and others DON'T.
                // If all have it (even if different) or none have it, hide it from "Differences Only".
                return hasPresent && hasAbsent;
            });

            return { ...cat, rows: extraRows };
        }).filter(cat => cat.rows.length > 0);
    }, [unifiedCats, showDifferencesOnly, visibleModels, getSpecValue]);

    const renderValue = (val) => {
        if (!val || val === '—') return <span className="ck ck-n">—</span>;
        if (val === '✓' || val.toLowerCase() === 'check') return <span className="ck ck-y">✓</span>;
        return <div className="v">{val}</div>;
    };

    const toggleSection = (cat) => {
        setOpenSecs(prev => {
            const next = new Set(prev);
            if (next.has(cat.title)) next.delete(cat.title);
            else next.add(cat.title);
            return next;
        });
    };

    const handleDownloadPdf = () => {
        setIsPdfReady(true);
        generateVehicleSpecsPDF({
            lang,
            vehicle,
            models: visibleModels,
            unifiedCats: filteredCatsForDisplay,
            getSpecValue,
            trans
        });
        setTimeout(() => setIsPdfReady(false), 2000);
    };

    return (
        <Fragment>
            <section className="specs-hero">
                <div className="specs-hero-bg"></div>
                <div className="specs-hero-grid"></div>
                <div className="specs-hero-content">
                    <div className="specs-hero-eyebrow">
                        <b>{activeModel?.brand || vehicle?.brand || "TOYOTA"}</b> · {trans("info:company_name")}
                    </div>
                    <h1 className="specs-hero-h1">
                        {isLoading ? <Skeleton width={200} /> : (activeModel?.group?.toUpperCase() || vehicle?.group?.toUpperCase())}
                        <span className="yr">{isLoading ? <Skeleton width={60} /> : (activeModel?.year || vehicle?.year)}</span>
                    </h1>
                    <p className="specs-hero-desc opacity-80 leading-relaxed font-light">
                        {trans("vehicales:specs_desc", "اختر الفئة المثالية — المواصفات التفصيلية لكل فئة متاحة.")}
                    </p>
                </div>
                <div className="specs-hero-car-wrap">
                    {isLoading ? (
                        <Skeleton height={250} width={400} borderRadius={20} />
                    ) : (
                        <img
                            className="specs-hero-car"
                            src={activeModel?.imagePath ? config.mainURL + activeModel.imagePath : (vehicle?.imagePath ? config.mainURL + vehicle.imagePath : config.mainURL + backUpImage)}
                            alt={activeModel?.group || vehicle?.group}
                        />
                    )}
                    <div className="specs-hero-glow"></div>
                </div>
            </section>

            <main className="container mt-16 mb-64">
                {isLoading ? (
                    <div className="w-100 flex flex-col gap-8">
                        <Skeleton height={60} borderRadius={12} />
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Skeleton height={400} borderRadius={20} />
                            <Skeleton height={400} borderRadius={20} />
                        </div>
                    </div>
                ) : models?.length > 0 ? (
                    <Fragment>
                        {/* Trim Names / Chips Bar */}
                        {models.length > 1 && (
                            <div className="trim-names-bar">
                                <div className="trim-names-bar-head">
                                    <div className="title-set">
                                        <span>{trans("vehicales:available_trims", "الفئات المتاحة")}</span>
                                        <div className="count-badge">{models.length} {trans("vehicales:trims", "فئة")}</div>
                                    </div>
                                    <div className="hint">{trans("vehicales:choose_trim_hint", "اختر فئة لعرض تفاصيلها")}</div>
                                </div>
                                {useTabbedView && (
                                    <div className="premium-tabs-row">
                                        <div className="premium-tabs-wrap">
                                            <div className="p-tabs">
                                                <button
                                                    className={`p-tab ${filterType === 'hybrid' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setFilterType('hybrid');
                                                        setViewMode('single');
                                                    }}
                                                >
                                                    <span className="p-tab-inner">
                                                        <img src="/assets/icons/specs/petrol.svg" alt="" className="ico active-hybrid" />
                                                        {trans("car:hybrid")}
                                                    </span>
                                                </button>
                                                <button
                                                    className={`p-tab ${filterType === 'conventional' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setFilterType('conventional');
                                                        setViewMode('single');
                                                    }}
                                                >
                                                    <span className="p-tab-inner">
                                                        <img src="/assets/icons/specs/engine.svg" alt="" className="ico" />
                                                        {trans("vehicales:others", "بقية الفئات")}
                                                    </span>
                                                </button>
                                                <div className={`p-tabs-indicator ${filterType}`}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="chips-wrap mt-6">
                                    <Swiper
                                        slidesPerView={"auto"}
                                        spaceBetween={12}
                                        centerInsufficientSlides={true}
                                        slidesOffsetBefore={20}
                                        slidesOffsetAfter={20}
                                        className="chips-swiper"
                                        style={{ padding: '8px 0' }}
                                    >
                                        {/* 
                                         <SwiperSlide style={{ width: 'auto' }}>
                                             <button
                                                 className={`chip ${viewMode === 'compare' && !showDifferencesOnly ? 'active shadow-lg' : 'border-dashed'}`}
                                                 onClick={() => {
                                                     setViewMode('compare');
                                                     setShowDifferencesOnly(false);
                                                     setIsPdfReady(false);
                                                 }}
                                                 style={viewMode === 'compare' && !showDifferencesOnly ? { borderColor: 'var(--g)', background: 'rgba(24, 169, 78, 0.05)', whiteSpace: 'nowrap' } : { borderStyle: 'dashed', whiteSpace: 'nowrap' }}
                                             >
                                                 <span className="dot" style={{ backgroundColor: 'var(--g)' }}></span>
                                                 <span className="font-black">{trans("vehicales:compare_all", "مقارنة الكل")}</span>
                                             </button>
                                         </SwiperSlide>

                                         <SwiperSlide style={{ width: 'auto' }}>
                                             <button
                                                 className={`chip ${viewMode === 'compare' && showDifferencesOnly ? 'active shadow-md' : 'border-dashed'}`}
                                                 onClick={() => {
                                                     setViewMode('compare');
                                                     setShowDifferencesOnly(true);
                                                     setIsPdfReady(false);
                                                 }}
                                                 style={viewMode === 'compare' && showDifferencesOnly ? { borderColor: '#23cc4f', background: 'rgba(35, 204, 79, 0.05)', whiteSpace: 'nowrap' } : { borderStyle: 'dashed', whiteSpace: 'nowrap' }}
                                             >
                                                 <span className="dot" style={{ backgroundColor: '#23cc4f' }}></span>
                                                 <span className="font-black">{trans("vehicales:differences_only", "المختلفة فقط")}</span>
                                             </button>
                                         </SwiperSlide>
*/}

                                        {!useTabbedView && showTypeFilters && (
                                            <SwiperSlide style={{ width: 'auto' }}>
                                                <div className="filter-group flex gap-2 mr-4 pr-4 border-r border-gray-200" style={{ whiteSpace: 'nowrap' }}>
                                                    <button
                                                        className={`chip-mini ${filterType === 'all' ? 'active' : ''}`}
                                                        onClick={() => setFilterType('all')}
                                                    >
                                                        {trans("vehicales:all", "الكل")}
                                                    </button>
                                                    <button
                                                        className={`chip-mini ${filterType === 'hybrid' ? 'active' : ''}`}
                                                        onClick={() => setFilterType('hybrid')}
                                                    >
                                                        {trans("car:hybrid")}
                                                    </button>
                                                    <button
                                                        className={`chip-mini ${filterType === 'conventional' ? 'active' : ''}`}
                                                        onClick={() => setFilterType('conventional')}
                                                    >
                                                        {trans("vehicales:automatic", "أوتوماتيك")}
                                                    </button>
                                                </div>
                                            </SwiperSlide>
                                        )}

                                        {filteredModels.map(m => (
                                            <SwiperSlide key={m.productTarget} style={{ width: 'auto' }}>
                                                <button
                                                    className={`chip ${viewMode === 'single' && selectedModelId === m.productTarget ? 'active shadow-lg' : 'border-dashed'}`}
                                                    onClick={() => {
                                                        if (viewMode === 'single' && selectedModelId === m.productTarget) return;
                                                        setSelectedModelId(m.productTarget);
                                                        setViewMode('single');
                                                        setIsPdfReady(false);
                                                    }}
                                                    style={{ borderStyle: viewMode === 'single' && selectedModelId === m.productTarget ? 'solid' : 'dashed', whiteSpace: 'nowrap' }}
                                                >
                                                    <span className="dot" style={{ backgroundColor: m.hybird ? '#1b8f47' : '#1565c0' }}></span>
                                                    {m.modelDescription}
                                                    <span className="badge">{m.hybird ? trans("car:hybrid") : ""}</span>
                                                </button>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </div>
                        )}

                        {/* Selected Trim Card (Visible only in single mode) */}
                        {viewMode === 'single' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8 bounce-in">
                                <div className="specs-tile active p-6 flex flex-col items-center">
                                    <img src={activeModel?.imagePath ? config.mainURL + activeModel.imagePath : config.mainURL + backUpImage} alt={activeModel?.modelDescription || activeModel?.group} className="w-full h-48 object-contain mb-4" />
                                    <h3 className="text-28 font-black text-green">{activeModel?.modelDescription}</h3>
                                    <div className="text-24 font-bold mt-2"><Price price={activeModel?.minPrice} /></div>
                                    <div className="flex flex-wrap justify-center gap-6 mt-6">
                                        <div className="flex flex-col items-center gap-1">
                                            <img src="/assets/icons/specs/horse-power.svg" alt="hp" className="w-9 h-9 filter-green" />
                                            <span className="text-14 font-bold text-gray-500">{activeModel?.hp} {trans("car:horse")}</span>
                                        </div>
                                        <div className="flex flex-col items-center gap-1">
                                            <img src="/assets/icons/specs/seats.svg" alt="seats" className="w-9 h-9 filter-green" />
                                            <span className="text-14 font-bold text-gray-500">{activeModel?.seatsNumber} {trans("car:seats")}</span>
                                        </div>
                                        <div className="flex flex-col items-center gap-1">
                                            <img src="/assets/icons/specs/petrol.svg" alt="petrol" className="w-9 h-9 filter-green" />
                                            <span className="text-14 font-bold text-gray-500">{activeModel?.hybird ? trans("car:hybrid") : trans("vehicales:veh-petrol")}</span>
                                        </div>
                                        <div className="flex flex-col items-center gap-1">
                                            <img src="/assets/icons/specs/cylinder.svg" alt="cylinder" className="w-9 h-9 filter-green" />
                                            <span className="text-14 font-bold text-gray-500">{activeModel?.cylinders || vehicle?.cylinders || "-"} {trans("car:cylinders")}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-gray-100 dark:border-zinc-800 p-10 flex flex-col justify-between shadow-lg relative overflow-hidden">
                                    {/* Accent Background Glow */}
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-green/5 blur-3xl rounded-full -mr-32 -mt-32 pointer-events-none"></div>

                                    <div className="flex-1 relative z-10">
                                        <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-4">
                                            <div className="w-full md:w-auto">
                                                <h4 className="text-14 uppercase tracking-[0.25em] font-black mb-3" style={{ color: "#18a94e" }}>
                                                    {trans("vehicales:selected_trim", "الفئة المختارة")}
                                                </h4>
                                                <h3 className="text-20 font-black text-black dark:text-white leading-tight">
                                                    {activeModel?.modelDescription}
                                                </h3>
                                            </div>
                                            <div className="text-left md:text-right w-full md:w-auto bg-green/5 md:bg-transparent p-4 md:p-0 rounded-2xl">
                                                <span className="text-12 uppercase font-black text-gray-400 block mb-1">
                                                    {trans("car:starting_at", "يبدأ من")}
                                                </span>
                                                <div className="text-36 font-black text-green leading-none">
                                                    <Price price={activeModel?.minPrice} style={{ color: "#18a94e", fontSize: "3xl" }} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="h-[2px] bg-gradient-to-r from-green/20 via-green/5 to-transparent w-full mb-8"></div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-8">
                                            {activeModel?.note?.split('\n').filter(f => f.trim() !== '').slice(0, 12).map((note, idx) => (
                                                <div key={idx} className="flex items-start gap-4 group">
                                                    <div className="w-6 h-6 rounded-full bg-green text-white flex items-center justify-center flex-shrink-0 mt-1 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                                        <span className="text-12 font-bold select-none" style={{ color: "#23c15f" }}>✓</span>
                                                    </div>
                                                    <span className="text-16 font-bold text-gray-700 dark:text-gray-300 leading-snug">
                                                        {note.startsWith('-') ? note.substring(1).trim() : note}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-10 pt-6 border-t border-gray-50 dark:border-zinc-800/50 flex items-center gap-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-green animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Comparison Card Zone (Visible only in compare mode) */}
                        {viewMode === 'compare' && (
                            mode === 'few' ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                                    {filteredModels.map(m => (
                                        <div
                                            key={m.productTarget}
                                            className={`specs-tile p-4 cursor-pointer transition-all ${selectedModelId === m.productTarget ? 'border-2 border-green' : 'border border-gray-100'}`}
                                            onClick={() => setSelectedModelId(m.productTarget)}
                                        >
                                            <div className="text-center">
                                                <div className="h-32 flex items-center justify-center mb-4">
                                                    <img src={m.imagePath ? config.mainURL + m.imagePath : config.mainURL + backUpImage} alt={m.modelDescription} className="max-w-full max-h-full object-contain" />
                                                </div>
                                                <h3 className="text-xl font-black">{m.modelDescription}</h3>
                                                <div className="text-green font-bold text-lg mt-2"><Price price={m.minPrice} /></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="relative mb-8 group">
                                    <div className="flex overflow-x-auto gap-4 pb-4 no-scrollbar scroll-smooth">
                                        {filteredModels.map(m => (
                                            <div
                                                key={m.productTarget}
                                                className={`flex-none w-[280px] specs-tile p-4 cursor-pointer transition-all ${selectedModelId === m.productTarget ? 'border-2 border-green shadow-lg' : 'border border-gray-100'}`}
                                                onClick={() => setSelectedModelId(m.productTarget)}
                                            >
                                                <div className="text-center">
                                                    <div className="h-32 flex items-center justify-center mb-4">
                                                        <img src={m.imagePath ? config.mainURL + m.imagePath : config.mainURL + backUpImage} alt={m.modelDescription} className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                    <h3 className="text-lg font-black">{m.modelDescription}</h3>
                                                    <div className="text-green font-bold text-md mt-1"><Price price={m.minPrice} /></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="absolute top-1/2 left-4 -translate-y-1/2 bg-black text-white text-10 font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        {lang === 'ar' ? 'مرر للمزيد ←' : 'Scroll for more →'}
                                    </div>
                                </div>
                            )
                        )}

                        <div className="flex justify-end mb-8">
                            <button className="pdf-export-btn" onClick={handleDownloadPdf} disabled={isPdfReady}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                {isPdfReady ? trans("vehicales:generating", "جاري التحميل...") : trans("vehicales:prepare_pdf_specs", "تنزيل المواصفات PDF")}
                            </button>
                        </div>

                        {/* Spec Sections */}
                        {filteredCatsForDisplay.map((cat, idx) => {
                            const isOpen = openSecs.has(cat.title);
                            return (
                                <div key={idx} className={`spec-sec-v2 ${!isOpen ? 'shut' : ''}`}>
                                    <div className="sh" onClick={() => {
                                        if (isMobile) {
                                            setActiveSpecCat(cat);
                                        } else {
                                            toggleSection(cat);
                                        }
                                    }}>
                                        <div className="sh-icon" style={{ backgroundColor: `rgba(24, 169, 78, 0.05)`, color: `#18a94e` }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                                        </div>
                                        <div className="sh-meta">
                                            <span className="sh-title">{cat.title}</span>
                                        </div>
                                        <span className="sh-arr">{isMobile ? '←' : '▾'}</span>
                                    </div>
                                    {!isMobile && (
                                        <div className="sb">
                                            <div className="tbl-wrap">
                                                <table className="stbl">
                                                    <thead>
                                                        <tr>
                                                            <th>{trans("vehicales:spec_label", "المواصفة")}</th>
                                                            {visibleModels.map(m => (
                                                                <th key={m.productTarget} style={{ color: m.hybird ? '#1b8f47' : '#1565c0' }}>{m.modelDescription}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cat.rows.map((rowLabel, rIdx) => {
                                                            const vals = visibleModels.map(m => getSpecValue(m.productTarget, cat.title, rowLabel));
                                                            const hasValue = vals.some(v => v !== null && v !== '—');
                                                            if (!hasValue) return null;

                                                            return (
                                                                <tr key={rIdx}>
                                                                    <td>{rowLabel}</td>
                                                                    {vals.map((v, vIdx) => (
                                                                        <td key={vIdx}>{renderValue(v)}</td>
                                                                    ))}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <div className="specs-fcta">
                            <div className="specs-fcta-text">
                                <h3>{trans("info:ready_to_decide", "جاهز لاتخاذ قرارك؟")}</h3>
                                <p>{trans("info:contact_sales_desc", "تواصل مع فريق المبيعات في المنطقة الشرقية — الخبر والدمام")}</p>
                            </div>
                            <div className="specs-fcta-btns">
                                <ReactWhatsapp
                                    className="vh-cta wa quote-wa"
                                    number="+966920018996"
                                    message={lang === "ar" ?
                                        `أنا مهتم بطلب عرض سعر لـ ${activeModel?.modelDescription || activeModel?.group} ${activeModel?.year || vehicle?.year} ${activeModel?.brand || vehicle?.brand}` :
                                        `I am interested in a quote for the ${activeModel?.modelDescription || activeModel?.group} ${activeModel?.year || vehicle?.year} ${activeModel?.brand || vehicle?.brand}`}
                                >
                                    <svg viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>
                                    {trans("info:request_quote", "طلب عرض سعر")}
                                </ReactWhatsapp>

                                <ReactWhatsapp
                                    className="vh-cta wa test-wa"
                                    number="+966920018996"
                                    message={lang === "ar" ?
                                        `أرغب في معرفة التفاصيل لـ ${activeModel?.modelDescription || activeModel?.group} ${activeModel?.year || vehicle?.year} ${activeModel?.brand || vehicle?.brand}` :
                                        `Interested in details for the ${activeModel?.modelDescription || activeModel?.group} ${activeModel?.year || vehicle?.year} ${activeModel?.brand || vehicle?.brand}`}
                                >
                                    <svg viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>
                                    {trans("info:details", "تفاصيل")}
                                </ReactWhatsapp>
                            </div>
                        </div>
                    </Fragment>
                ) : (
                    <div className="w-100 py-20">
                        <EmptyState message={trans("vehicales:no-specs-data") || "No specifications data available for this vehicle"} />
                    </div>
                )}
            </main>

            {/* ═══ MOBILE SPECS MODAL ═══ */}
            {activeSpecCat && (
                <div className="spec-modal-overlay" onClick={() => setActiveSpecCat(null)}>
                    <div className="spec-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="spec-modal-header">
                            <h2 className="spec-modal-title">{activeSpecCat.title}</h2>
                            <button className="spec-modal-close" onClick={() => setActiveSpecCat(null)}>✕</button>
                        </div>
                        <div className="spec-modal-body">
                            <div className="spec-mobile-cards">
                                {filteredCatsForDisplay.find(c => c.title === activeSpecCat.title)?.rows.map((rowLabel, rIdx) => {
                                    const vals = visibleModels.map(m => ({
                                        name: m.modelDescription,
                                        isHybrid: m.hybird,
                                        value: getSpecValue(m.productTarget, activeSpecCat.title, rowLabel)
                                    }));
                                    const hasValue = vals.some(v => v.value !== null && v.value !== '—');
                                    if (!hasValue) return null;

                                    return (
                                        <div key={rIdx} className="spec-mob-card">
                                            <div className="spec-mob-label">{rowLabel}</div>
                                            <div className="spec-mob-values">
                                                {vals.map((v, vIdx) => (
                                                    <div key={vIdx} className="spec-mob-value-row">
                                                        <div className="model-name">
                                                            <span className={`type-dot ${v.isHybrid ? 'hybrid' : 'petrol'}`}></span>
                                                            {v.name}
                                                        </div>
                                                        <div className="model-val">{renderValue(v.value)}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}
